import { DevicePageRespData } from "api/types/device";
import { t } from "i18next";

import { notifySimple } from "providers/toast";
import { useCallback, useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { accountSiteList } from "store/appSelectors";

import Combobox from "widgets/shared/inputs/combobox";
import Table from "widgets/controls/tables/genericTable";
import { reportViewGenerated } from "api/report";
import defineReportsTablecolumns, {
  ReportsPageTableModel,
} from "models/tables/reportsPage";
import { MdRefresh } from "react-icons/md";
import CircleButton from "widgets/controls/buttons/circle-button";
import { useSearchParams } from "react-router-dom";
import { useNotificationDialog } from "providers/notification-dialog-provider";
import ScheduleRecipientsDialog from "widgets/dialogs/schedule";

const ReportsView = () => {
  const { notify } = useNotificationDialog();
  const [searchParams] = useSearchParams();
  // Extract the filter parameter
  const id = searchParams.get("id");

  const userInfo = useSelector(accountSiteList, shallowEqual);
  const columns = defineReportsTablecolumns(
    (info) => {
      const row = info.row.original;

      setSelected(row);
      setIsOpen(true);
    },
    (info) => {
      const row = info.row.original;
      setSelected(row);
      setIsOpen(true);
    },
    (info) => {
      const row = info.row.original;
      downloadPdf(row.pdfUrl);
    },
    (info) => {
      const row = info.row.original;
      downloadExcel(row.excelUrl);
    },
    userInfo
  );

  const [state, setState] = useState({
    siteId: id || "",
  });
  const [dataSource, setDataSource] = useState<DevicePageRespData[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<ReportsPageTableModel>();

  const fetchData = useCallback(async () => {
    if (state.siteId) {
      setIsLoading(true);
      const { data, error, isLoading } = await reportViewGenerated(
        state.siteId
      );
      setIsLoading(isLoading);
      if (!error) {
        setDataSource(data || []);
      } else {
        notifySimple(t("toast.failed.data"), "error");
      }
    } else {
      setDataSource([]);
    }
  }, [state.siteId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (id) {
      notify({
        header: t("reports.view.title"),
        message: t("reports.view.description"),
      });
    }
  }, [id]);

  // Download PDF file
  const downloadPdf = (file: string) => {
    const link = document.createElement("a");
    link.href = file;
    link.target = "_blank";
    link.download = "report.pdf";
    link.click();
  };

  // Download Excel file
  const downloadExcel = (file: string) => {
    const link = document.createElement("a");
    link.href = file;
    link.target = "_blank";
    link.download = "report.xlsx";
    link.click();
  };

  return (
    <>
      {isOpen && (
        <ScheduleRecipientsDialog
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          onSuccess={() => setIsOpen(false)}
          context={"view"}
          values={selected}
        />
      )}
      <div className="mt-4">
        <Table
          offset={70}
          filterbar={
            <div className="flex h-full w-full flex-col gap-y-3">
              <div className="flex justify-between">
                <div className="">
                  <Combobox
                    filter
                    id="device-select-site"
                    placeholder={t("common.select.site")}
                    variant="solid"
                    color="secondary"
                    value={state.siteId}
                    onChange={(item) => {
                      setState((prevState) => ({
                        ...prevState,
                        siteId: item,
                      }));
                    }}
                    items={userInfo}
                    labelKey="name"
                    valueKey="id"
                    sizes="md"
                    rounded="rounded-xl"
                  />
                </div>
                <CircleButton
                  onClick={() => {
                    fetchData();
                  }}
                  icon={MdRefresh}
                  size="md"
                  color="secondary"
                  variant="ghost"
                  tooltip={t("tooltip.refresh")}
                  tooltipPlacement="bottom"
                  disabled={isLoading}
                />
              </div>
            </div>
          }
          noDataLabel={
            state.siteId
              ? t("table.status.noData")
              : t("toast.no.site.selected")
          }
          loading={isLoading}
          data={dataSource}
          columns={columns}
          compact
          totalRecords={dataSource.length}
        />
      </div>
    </>
  );
};

export default ReportsView;
